import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import LeftPanel from "@/components/LeftPanel";
import CenterPanel from "@/components/CenterPanel";
import RightPanel from "@/components/RightPanel";
import SettingsDialog from "@/components/SettingsDialog";
import BlacklistDialog from "@/components/BlacklistDialog";
import PnLDialog from "@/components/PnLDialog";
import LiveTradeDialog from "@/components/LiveTradeDialog";

const ALL = ["Solana", "Base", "BNB", "Ethereum", "Monad", "Robinhood"];

export default function Dashboard() {
  const { logout } = useAuth();
  const [chains, setChains] = useState([]);
  const [selected, setSelected] = useState(ALL);
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [positions, setPositions] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [pnlOpen, setPnlOpen] = useState(false);
  const [solStatus, setSolStatus] = useState(null);
  const [liveCandidate, setLiveCandidate] = useState(null);
  const [liveSubmitting, setLiveSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tradingAddr, setTradingAddr] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const filterTimer = useRef(null);

  const loadStatic = useCallback(async () => {
    try {
      const [ch, cfg, bl, sol] = await Promise.all([
        api.get("/chains"), api.get("/config"), api.get("/blacklist"), api.get("/solana/status"),
      ]);
      setChains(ch.data); setConfig(cfg.data); setBlacklist(bl.data); setSolStatus(sol.data);
      setSelected(cfg.data.enabled_chains || ALL);
    } catch (e) { /* handled by auth */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const [st, stt, cand, pos, ex, rej] = await Promise.all([
        api.get("/bot/state"), api.get("/stats"),
        api.get(`/candidates?chains=${selected.join(",")}`),
        api.get("/positions?status=active"), api.get("/executions"), api.get("/signals/rejected"),
      ]);
      setState(st.data); setStats(stt.data); setCandidates(cand.data);
      setPositions(pos.data); setExecutions(ex.data); setRejected(rej.data);
    } catch (e) { /* ignore transient */ }
  }, [selected]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => {
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [poll]);

  const ctrl = async (path, msg) => {
    try {
      const { data } = await api.post(`/bot/${path}`);
      setState(data);
      toast.success(msg);
      poll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleChain = (name) => {
    const next = selected.includes(name) ? selected.filter((c) => c !== name) : [...selected, name];
    if (next.length === 0) return;
    setSelected(next);
    api.put("/config", { enabled_chains: next }).catch(() => {});
  };

  const onFilterChange = (key, val) => {
    setConfig((c) => ({ ...c, [key]: val }));
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      api.put("/config", { [key]: val }).then(() => poll()).catch(() => {});
    }, 500);
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/config", patch);
      setConfig(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onTrade = async (c) => {
    setTradingAddr(c.address);
    try {
      await api.post("/positions/open", { address: c.address, chain: c.chain });
      toast.success(`Opened ${c.symbol}`);
      poll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setTradingAddr(null);
  };

  const onClose = async (id) => {
    setClosingId(id);
    try {
      await api.post(`/positions/${id}/close`);
      toast.success("Position closed");
      poll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setClosingId(null);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try { await api.post("/ingest/run"); toast.success("Signals refreshed"); poll(); }
    catch (e) { toast.error("Refresh failed"); }
    setRefreshing(false);
  };

  const onEmergency = async () => {
    await ctrl("emergency-stop", "EMERGENCY STOP executed");
  };

  const onLive = (c) => setLiveCandidate(c);

  const confirmLive = async () => {
    setLiveSubmitting(true);
    try {
      const { data } = await api.post("/live/buy", { address: liveCandidate.address, chain: liveCandidate.chain });
      toast.success(`LIVE buy sent · ${data.signature ? data.signature.slice(0, 8) + "…" : data.status}`);
      setLiveCandidate(null);
      poll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setLiveSubmitting(false);
  };

  const addBlacklist = async (value) => {
    try { const { data } = await api.post("/blacklist", { value }); setBlacklist((b) => [data, ...b]); }
    catch (e) { toast.error("Failed to add"); }
  };
  const delBlacklist = async (id) => {
    try { await api.delete(`/blacklist/${id}`); setBlacklist((b) => b.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Failed to delete"); }
  };

  if (!config) return <div className="h-screen flex items-center justify-center bg-[#09090b] text-[#a1a1aa] font-mono text-sm">Loading terminal…</div>;

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-[#09090b] text-[#f4f4f5]">
      <TopBar state={state} stats={stats} mode={config.mode}
        onStart={() => ctrl("start", "Bot started")} onPause={() => ctrl("pause", "Bot paused")}
        onResume={() => ctrl("resume", "Bot resumed")} onStop={() => ctrl("stop", "Bot stopped")}
        onEmergency={onEmergency} onLogout={logout} />
      <div className="flex-1 grid grid-cols-12 gap-0 overflow-hidden">
        <LeftPanel chains={chains} selected={selected} toggleChain={toggleChain} config={config}
          onFilterChange={onFilterChange} onOpenSettings={() => setSettingsOpen(true)}
          onOpenBlacklist={() => setBlacklistOpen(true)} onRefresh={onRefresh} refreshing={refreshing}
          onOpenPnl={() => setPnlOpen(true)} solStatus={solStatus} />
        <CenterPanel candidates={candidates} onTrade={onTrade} tradingAddr={tradingAddr}
          onLive={onLive} solConfigured={solStatus?.configured} />
        <RightPanel positions={positions} executions={executions} rejected={rejected}
          onClose={onClose} closingId={closingId} />
      </div>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} config={config} onSave={saveSettings} />
      <BlacklistDialog open={blacklistOpen} onOpenChange={setBlacklistOpen} items={blacklist}
        onAdd={addBlacklist} onDelete={delBlacklist} />
      <PnLDialog open={pnlOpen} onOpenChange={setPnlOpen} />
      <LiveTradeDialog open={!!liveCandidate} onOpenChange={(o) => !o && setLiveCandidate(null)}
        candidate={liveCandidate} config={config} solStatus={solStatus}
        onConfirm={confirmLive} submitting={liveSubmitting} />
    </div>
  );
}
